// Copyright 2024 Vajra. All rights reserved.
// Samyoga RAG Chat Widget - Natural language security queries

import { useState, useRef, useEffect, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import {
  ChatBubbleLeftRightIcon,
  PaperAirplaneIcon,
  XMarkIcon,
  SparklesIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  DocumentMagnifyingGlassIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { api } from '../lib/api';

// Message types
interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
  sources?: ChatSource[];
  findings?: SecurityFinding[];
  loading?: boolean;
}

interface ChatSource {
  type: 'event' | 'policy' | 'threat_intel' | 'device' | 'user';
  id: string;
  title: string;
  url?: string;
  snippet?: string;
}

interface SecurityFinding {
  severity: 'critical' | 'high' | 'medium' | 'low' | 'info';
  title: string;
  description: string;
  timestamp?: string;
  resource?: string;
}

// Suggested queries for quick access
const suggestedQueries = [
  {
    icon: ExclamationTriangleIcon,
    query: 'Show me recent security threats',
    category: 'Threats',
  },
  {
    icon: ShieldCheckIcon,
    query: 'What URLs were blocked today?',
    category: 'Blocks',
  },
  {
    icon: DocumentMagnifyingGlassIcon,
    query: 'Show DLP violations this week',
    category: 'DLP',
  },
  {
    icon: InformationCircleIcon,
    query: 'What is the M365 security posture?',
    category: 'M365',
  },
];

export default function SamyogaChat() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
    }
  }, [isOpen]);

  // Add welcome message on first open
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([
        {
          id: 'welcome',
          role: 'system',
          content:
            "Welcome to Samyoga Security Assistant! Ask me anything about your security data - threats, DLP events, blocked URLs, M365 posture, and more. I can search across all your security logs and provide insights.",
          timestamp: new Date(),
        },
      ]);
    }
  }, [isOpen, messages.length]);

  const sendMessage = async (query: string) => {
    if (!query.trim() || isLoading) return;

    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: query,
      timestamp: new Date(),
    };

    const loadingMessage: ChatMessage = {
      id: `loading-${Date.now()}`,
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      loading: true,
    };

    setMessages((prev) => [...prev, userMessage, loadingMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const response = await api.post('/samyoga/rag/query', { query });
      const data = response.data;

      const assistantMessage: ChatMessage = {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: data.answer || data.response || 'No response received.',
        timestamp: new Date(),
        sources: data.sources || [],
        findings: data.findings || [],
      };

      setMessages((prev) =>
        prev.filter((m) => !m.loading).concat(assistantMessage)
      );
    } catch (error) {
      console.error('Chat error:', error);

      const errorMessage: ChatMessage = {
        id: `error-${Date.now()}`,
        role: 'assistant',
        content:
          'Sorry, I encountered an error processing your query. Please try again.',
        timestamp: new Date(),
      };

      setMessages((prev) =>
        prev.filter((m) => !m.loading).concat(errorMessage)
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sendMessage(input);
  };

  const handleSuggestedQuery = (query: string) => {
    sendMessage(query);
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'text-red-700 bg-red-50 border-red-200';
      case 'high':
        return 'text-orange-700 bg-orange-50 border-orange-200';
      case 'medium':
        return 'text-yellow-700 bg-yellow-50 border-yellow-200';
      case 'low':
        return 'text-blue-700 bg-blue-50 border-blue-200';
      default:
        return 'text-gray-700 bg-gray-50 border-gray-200';
    }
  };

  return (
    <>
      {/* Floating Chat Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-blue-600 text-white shadow-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all hover:scale-105"
        title="Samyoga Security Assistant"
      >
        <ChatBubbleLeftRightIcon className="h-7 w-7" />
      </button>

      {/* Chat Dialog */}
      <Transition.Root show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={setIsOpen}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-25 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-end p-4 sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-xl sm:mr-8 sm:mb-8">
                  {/* Header */}
                  <div className="flex items-center justify-between bg-blue-600 px-4 py-3">
                    <div className="flex items-center space-x-2">
                      <SparklesIcon className="h-6 w-6 text-white" />
                      <Dialog.Title className="text-lg font-semibold text-white">
                        Samyoga Security Assistant
                      </Dialog.Title>
                    </div>
                    <button
                      onClick={() => setIsOpen(false)}
                      className="rounded-md p-1 text-blue-200 hover:bg-blue-700 hover:text-white"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>

                  {/* Messages Area */}
                  <div className="h-96 overflow-y-auto p-4 space-y-4 bg-gray-50">
                    {messages.map((message) => (
                      <div
                        key={message.id}
                        className={clsx(
                          'flex',
                          message.role === 'user'
                            ? 'justify-end'
                            : 'justify-start'
                        )}
                      >
                        <div
                          className={clsx(
                            'max-w-[85%] rounded-lg px-4 py-2 shadow-sm',
                            message.role === 'user'
                              ? 'bg-blue-600 text-white'
                              : message.role === 'system'
                              ? 'bg-blue-50 text-blue-800 border border-blue-200'
                              : 'bg-white text-gray-800 border border-gray-200'
                          )}
                        >
                          {message.loading ? (
                            <div className="flex items-center space-x-2">
                              <div className="animate-pulse flex space-x-1">
                                <div className="h-2 w-2 bg-gray-400 rounded-full animate-bounce" />
                                <div className="h-2 w-2 bg-gray-400 rounded-full animate-bounce delay-100" />
                                <div className="h-2 w-2 bg-gray-400 rounded-full animate-bounce delay-200" />
                              </div>
                              <span className="text-sm text-gray-500">
                                Searching security data...
                              </span>
                            </div>
                          ) : (
                            <>
                              <p className="text-sm whitespace-pre-wrap">
                                {message.content}
                              </p>

                              {/* Security Findings */}
                              {message.findings && message.findings.length > 0 && (
                                <div className="mt-3 space-y-2">
                                  <p className="text-xs font-semibold text-gray-500 uppercase">
                                    Security Findings
                                  </p>
                                  {message.findings.map((finding, idx) => (
                                    <div
                                      key={idx}
                                      className={clsx(
                                        'rounded-md p-2 border text-xs',
                                        getSeverityColor(finding.severity)
                                      )}
                                    >
                                      <div className="flex items-center justify-between">
                                        <span className="font-medium">
                                          {finding.title}
                                        </span>
                                        <span className="uppercase text-[10px] font-bold">
                                          {finding.severity}
                                        </span>
                                      </div>
                                      <p className="mt-1 text-gray-600">
                                        {finding.description}
                                      </p>
                                      {finding.timestamp && (
                                        <div className="mt-1 flex items-center text-gray-400">
                                          <ClockIcon className="h-3 w-3 mr-1" />
                                          {finding.timestamp}
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}

                              {/* Sources */}
                              {message.sources && message.sources.length > 0 && (
                                <div className="mt-3">
                                  <p className="text-xs font-semibold text-gray-500 uppercase mb-1">
                                    Sources
                                  </p>
                                  <div className="flex flex-wrap gap-1">
                                    {message.sources.map((source, idx) => (
                                      <span
                                        key={idx}
                                        className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600"
                                      >
                                        {source.type === 'event' && (
                                          <DocumentMagnifyingGlassIcon className="h-3 w-3 mr-1" />
                                        )}
                                        {source.type === 'threat_intel' && (
                                          <ExclamationTriangleIcon className="h-3 w-3 mr-1" />
                                        )}
                                        {source.type === 'policy' && (
                                          <ShieldCheckIcon className="h-3 w-3 mr-1" />
                                        )}
                                        {source.title}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>

                  {/* Suggested Queries (show when few messages) */}
                  {messages.length <= 1 && (
                    <div className="border-t border-gray-200 bg-white p-3">
                      <p className="text-xs font-medium text-gray-500 mb-2">
                        Suggested queries:
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {suggestedQueries.map((item, idx) => (
                          <button
                            key={idx}
                            onClick={() => handleSuggestedQuery(item.query)}
                            className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-200 transition-colors"
                          >
                            <item.icon className="h-3.5 w-3.5 mr-1.5 text-gray-500" />
                            {item.query}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Input Area */}
                  <form
                    onSubmit={handleSubmit}
                    className="border-t border-gray-200 bg-white p-3"
                  >
                    <div className="flex items-center space-x-2">
                      <input
                        ref={inputRef}
                        type="text"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        placeholder="Ask about security events, threats, policies..."
                        className="flex-1 rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        disabled={isLoading}
                      />
                      <button
                        type="submit"
                        disabled={isLoading || !input.trim()}
                        className={clsx(
                          'rounded-lg p-2 text-white transition-colors',
                          isLoading || !input.trim()
                            ? 'bg-gray-300 cursor-not-allowed'
                            : 'bg-blue-600 hover:bg-blue-700'
                        )}
                      >
                        <PaperAirplaneIcon className="h-5 w-5" />
                      </button>
                    </div>
                    <p className="mt-2 text-xs text-gray-400 text-center">
                      Powered by Samyoga RAG - Natural language security queries
                    </p>
                  </form>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
}
